import { encodeBase64Url } from './base64url';
import { sessionFromClaims } from './claims';
import { decodeJwt } from './jwt';
import { IdpAuthError, IdpProvider, IdpTokens } from './provider';

/**
 * O duble do IDP, ativo com `REACT_APP_AUTH_PROVIDER=mock`.
 *
 * Serve para rodar e testar o login sem subir o Keycloak — mesma razao do
 * `MockAuthProvider` do hostzone. Ele exercita o caminho de verdade do app: o
 * navegador sai daqui para `/auth/callback?code=…&state=…`, e a volta passa
 * pelo mesmo `sessionFromClaims` do provider real, que e o que garante que os
 * dois entreguem a mesma sessao.
 *
 * O que ele NAO faz, de proposito: assinar token de verdade. O `id_token` dele
 * e um JWT sem assinatura valida, e por isso `toSession` aqui le as claims sem
 * verificar — em producao quem le e o `verifyIdToken`, contra o JWKS.
 */

const TTL_PADRAO_S = 300;
const MS_POR_SEGUNDO = 1000;

export interface MockIdpProviderOptions {
  readonly clientId?: string;
  readonly issuer?: string;
  readonly accessTokenTtl?: number;
  /** Conta autenticada e sem vinculo — o que o auto-registro do IDP produz. */
  readonly semVinculo?: boolean;
  readonly now?: () => number;
  readonly falha?: 'exchange' | 'refresh';
}

function fakeJwt(claims: Record<string, unknown>): string {
  const encoder = new TextEncoder();
  const header = encodeBase64Url(encoder.encode(JSON.stringify({ alg: 'none', typ: 'JWT' })));
  const payload = encodeBase64Url(encoder.encode(JSON.stringify(claims)));
  return `${header}.${payload}.duble-sem-assinatura`;
}

export function createMockIdpProvider(options: MockIdpProviderOptions = {}): IdpProvider {
  const {
    clientId = 'sapron-frontend',
    issuer = 'https://idp.mock.seazone.test/realms/seazone',
    accessTokenTtl = TTL_PADRAO_S,
    semVinculo = false,
    now = () => Date.now(),
    falha,
  } = options;

  const emite = (nonce: string): IdpTokens => {
    const emitidoEm = Math.floor(now() / MS_POR_SEGUNDO);
    const claims = {
      iss: issuer,
      azp: clientId,
      aud: clientId,
      sub: semVinculo ? 'mock-sem-vinculo' : 'mock-proprietaria',
      nonce,
      iat: emitidoEm,
      exp: emitidoEm + accessTokenTtl,
      email: semVinculo ? 'ninguem@mock.seazone.test' : 'proprietaria@mock.seazone.test',
      name: semVinculo ? 'Conta Sem Vinculo' : 'Proprietaria Mock',
      ...(semVinculo ? { roles: [] } : { roles: ['Owner'], sapron_user_id: 900_001 }),
      permissions: semVinculo ? [] : ['financeiro:view', 'imoveis:view'],
    };
    return {
      accessToken: fakeJwt(claims),
      refreshToken: fakeJwt({ ...claims, typ: 'Refresh' }),
      idToken: fakeJwt(claims),
      expiresAt: emitidoEm + accessTokenTtl,
    };
  };

  return {
    async authorizeUrl({ state, redirectUri }) {
      // Volta direto para o callback do app: o duble nao tem tela de login, e
      // o que interessa provar e que o callback do app sabe fechar o fluxo.
      const url = new URL(redirectUri);
      url.searchParams.set('code', `mock-code-${state}`);
      url.searchParams.set('state', state);
      return url.toString();
    },

    async exchangeCode({ nonce }) {
      if (falha === 'exchange') {
        throw new IdpAuthError('mock_exchange_falhou', 'Duble configurado para falhar na troca.');
      }
      return emite(nonce);
    },

    async refresh(refreshToken) {
      if (falha === 'refresh') {
        throw new IdpAuthError('invalid_grant', 'Duble configurado para falhar no refresh.');
      }
      const { payload } = decodeJwt(refreshToken);
      return emite(typeof payload.nonce === 'string' ? payload.nonce : 'mock-nonce');
    },

    async toSession(tokens) {
      if (tokens.idToken === undefined) {
        throw new IdpAuthError('id_token_ausente', 'Duble respondeu sem id_token.');
      }
      return sessionFromClaims(decodeJwt(tokens.idToken).payload);
    },

    // `endSession` ausente de proposito: o duble nao tem sessao central para
    // encerrar, e e assim que o app descobre que o logout dele e so local.
  };
}
